import fetch from 'cross-fetch';
import * as apiValues from '../constants/apiValues';
import * as helpers from '../helpers';

const loadedEndpoints = {};
const POPULATABLE_FIELDS = new Set([
  'applicationSteps',
  'background',
  'backgroundImage',
  'backgroundVideo',
  'cardList',
  'cardListA',
  'cardListB',
  'cardListC',
  'cards',
  'carouselSlides',
  'category',
  'contact',
  'contacts',
  'currentProjects',
  'document',
  'events',
  'eventsRelatedProgram',
  'eventsRelatedProject',
  'eventsRelatedTag',
  'featuredProgram',
  'featuredProjects',
  'getInvolved',
  'heroCarousel',
  'icon',
  'iconHover',
  'image',
  'imageBackground',
  'imageLogo',
  'imageOptional',
  'linkMedia',
  'logo',
  'mCarousels',
  'mGetInvolved',
  'mHeaders',
  'mListCards',
  'mListNavs',
  'mProgramMapAd',
  'media',
  'mediaImage',
  'mediaVideo',
  'mobileContactLinks',
  'mobileLinks',
  'navigationLinks',
  'news',
  'newsRelatedProgram',
  'newsRelatedProject',
  'pageContentDetails',
  'paperForms',
  'photosAndVideos',
  'program',
  'programs',
  'project',
  'projectFeatureLocations',
  'projectFeatures',
  'projectPartners',
  'projectTags',
  'projectTypes',
  'projects',
  'projectstatus',
  'relatedProject',
  'relatedTag',
  'socialList',
  'submenuA',
  'submenuB',
  'submenuC',
  'tags',
  'thumbnail',
]);
/** Strapi 5 media attributes — use `populate[field]=true`, not `[populate]=*` (wildcard drops the file). */
const STRAPI_MEDIA_POPULATE_FIELDS = new Set([
  'backgroundImage',
  'backgroundVideo',
  'icon',
  'image',
  'imageOptional',
  'logo',
  'mediaImage',
  'thumbnail',
]);
const ARRAY_FIELDS = new Set([
  'applicationSteps',
  'cardList',
  'cardListA',
  'cardListB',
  'cardListC',
  'cards',
  'carouselSlides',
  'contacts',
  'events',
  'featuredProjects',
  'mCarousels',
  'mHeaders',
  'mListCards',
  'mListNavs',
  'mobileContactLinks',
  'mobileLinks',
  'navigationLinks',
  'news',
  'pageContentDetails',
  'paperForms',
  'photosAndVideos',
  'programs',
  'projectFeatureLocations',
  'projectFeatures',
  'projectPartners',
  'projectTags',
  'projectTypes',
  'projects',
  'socialList',
  'submenuA',
  'submenuB',
  'submenuC',
  'tags',
]);
/** Slug-based URL lookups should match regardless of path casing (LLSU2-53). */
const CASE_INSENSITIVE_FILTER_FIELDS = new Set(['slug']);

const RELATION_FILTER_FIELDS = new Set([
  'category',
  'contacts',
  'events',
  'eventsRelatedProgram',
  'eventsRelatedProject',
  'eventsRelatedTag',
  'featuredProgram',
  'mListNavs',
  'news',
  'newsRelatedProgram',
  'newsRelatedProject',
  'pageContentDetails',
  'program',
  'programs',
  'project',
  'projectFeatureLocations',
  'projectFeatures',
  'projectPartners',
  'projectTypes',
  'projectstatus',
  'relatedProject',
  'relatedTag',
  'tags',
]);

/** "CMS Name" -> cmsName (same rules as CMS migration) */
function attrToCamelCase(label) {
  const parts = String(label).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return label;
  if (parts.length === 1) {
    const p = parts[0];
    if (/^[A-Z0-9]+$/.test(p) && p.length > 1) return p.toLowerCase();
    return p.charAt(0).toLowerCase() + p.slice(1);
  }
  const p0 = parts[0];
  const first =
    /^[A-Z0-9]+$/.test(p0) && p0.length > 1
      ? p0.toLowerCase()
      : p0.charAt(0).toLowerCase() + p0.slice(1);
  const rest = parts
    .slice(1)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('');
  return first + rest;
}

function legacyKeyToCamelCase(key) {
  if (key === 'CMSName') return 'cmsName';
  if (!key || key.indexOf(' ') >= 0) return attrToCamelCase(key);
  return key.charAt(0).toLowerCase() + key.slice(1);
}

function mapStrapiEntity(entry) {
  if (entry == null) return entry;
  if (typeof entry !== 'object') return entry;
  const { id, documentId, attributes, ...rest } = entry;
  const attrs = attributes && typeof attributes === 'object' ? attributes : {};
  const docId = documentId || attrs.documentId || id;
  return {
    id: docId,
    documentId: docId,
    ...attrs,
    ...rest,
  };
}

/** Same-origin Strapi media URLs → path so `${getCmsPath()}${url}` works in the browser. */
function coerceCmsMediaPath(urlStr) {
  if (urlStr == null || urlStr === '') return urlStr;
  const s = String(urlStr).trim();
  if (!/^https?:\/\//i.test(s)) return s.startsWith('/') ? s : `/${s}`;
  try {
    const base = apiValues.getCmsPath().replace(/\/+$/, '');
    const u = new URL(s);
    const b = new URL(base);
    if (u.hostname === b.hostname) return u.pathname + (u.search || '');
  } catch (_) {
    /* ignore */
  }
  return s;
}

/** Strapi upload / media object → legacy-friendly shape (url + alt). */
function normalizeMediaNode(node) {
  if (node == null || typeof node !== 'object') return node;
  const inner = node.attributes && typeof node.attributes === 'object' ? node.attributes : node;
  let url = inner.url != null ? String(inner.url) : node.url != null ? String(node.url) : undefined;
  if (url !== undefined) url = coerceCmsMediaPath(url);
  if (url === undefined && !inner.mime && !inner.provider) return node;
  const alt =
    inner.alternativeText != null
      ? inner.alternativeText
      : inner.caption != null
        ? inner.caption
        : node.alternativeText;
  return {
    ...node,
    ...inner,
    id: node.documentId || inner.documentId || node.id || inner.id,
    documentId: node.documentId || inner.documentId || node.id,
    url,
    alternativeText: alt,
  };
}

function deepNormalizeStrapi(value, depth = 0) {
  if (value == null || depth > 12) return value;
  if (Array.isArray(value)) return value.map((v) => deepNormalizeStrapi(v, depth + 1));
  if (typeof value !== 'object') return value;
  if (Object.prototype.hasOwnProperty.call(value, 'data')) {
    const { data } = value;
    if (Array.isArray(data)) return data.map((e) => deepNormalizeStrapi(mapStrapiEntity(e), depth + 1));
    if (data == null) return null;
    return deepNormalizeStrapi(mapStrapiEntity(data), depth + 1);
  }
  if (value.attributes && (value.documentId != null || value.id != null)) {
    return deepNormalizeStrapi(mapStrapiEntity(value), depth + 1);
  }
  // Flat Strapi 5 relations keep numeric `id` beside `documentId`; remap so filters match list ids.
  if (
    value.documentId != null &&
    value.id != null &&
    value.id !== value.documentId
  ) {
    return deepNormalizeStrapi(mapStrapiEntity(value), depth + 1);
  }
  if (
    typeof value.url === 'string' ||
    value.mime != null ||
    value.provider === 'local' ||
    value.provider === 'aws-s3'
  ) {
    return normalizeMediaNode(value);
  }
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    out[k] = deepNormalizeStrapi(v, depth + 1);
  }
  return out;
}

/** Flatten Strapi 5 REST { data: [{ id, documentId, ...attributes }] } into legacy-like objects */
export const unwrapStrapiResponse = (json) => {
  if (!json || typeof json !== 'object') return json;
  if (!Object.prototype.hasOwnProperty.call(json, 'data')) return json;
  const { data } = json;
  const mapEntry = (entry) => {
    if (entry == null) return entry;
    if (typeof entry !== 'object') return entry;
    const flat = mapStrapiEntity(entry);
    return deepNormalizeStrapi(flat, 0);
  };
  if (Array.isArray(data)) return data.map(mapEntry);
  return mapEntry(data);
};

/** Build query string for Strapi 5 from legacy flat filter object */
export function encodeStrapiV5Params(data) {
  const parts = [];
  const add = (k, v) => {
    if (v === undefined || v === null) return;
    if (v === '') return;
    parts.push([k, String(v)]);
  };

  for (const [key, val] of Object.entries(data || {})) {
    if (key === 'populate' || key.startsWith('filters[') || key.startsWith('populate[')) {
      add(key, val);
      continue;
    }
    if (key === '_limit') {
      // Strapi 5 clamps invalid/negative limits to 1 — do not send -1 for "all".
      const lim = val === 0 || val === -1 ? apiValues.STRAPI_REST_MAX_LIMIT : val;
      add('pagination[limit]', lim);
      continue;
    }
    if (key === '_start') {
      add('pagination[start]', val);
      continue;
    }
    if (key === '_sort') {
      const sort = String(val).replace(/^([^:]+)(:.*)?$/, (_, field, dir = '') => {
        return `${legacyKeyToCamelCase(field)}${dir}`;
      });
      add('sort', sort);
      continue;
    }
    const m = key.match(/^(.+)_(contains|ne|gt|lt|gte|lte)$/);
    if (m) {
      const field = attrToCamelCase(m[1].replace(/_/g, ' '));
      const op = m[2];
      const opMap = {
        contains: '$containsi',
        ne: '$ne',
        gt: '$gt',
        lt: '$lt',
        gte: '$gte',
        lte: '$lte',
      };
      const strapiOp = opMap[op] || '$eq';
      add(`filters[${field}][${strapiOp}]`, val);
      continue;
    }
    if (key.endsWith('_in')) {
      const field = attrToCamelCase(key.slice(0, -3).replace(/_/g, ' '));
      if (val === undefined || val === null || val === '') continue;
      const arr = Array.isArray(val) ? val : String(val).split(',').filter(Boolean);
      if (!arr.length) continue;
      arr.forEach((item, idx) => {
        if (RELATION_FILTER_FIELDS.has(field)) {
          add(`filters[${field}][documentId][$in][${idx}]`, item);
        } else {
          add(`filters[${field}][$in][${idx}]`, item);
        }
      });
      continue;
    }
    const field = legacyKeyToCamelCase(key);
    if (/^[A-Za-z][A-Za-z0-9_ ]*$/.test(key)) {
      if (RELATION_FILTER_FIELDS.has(field)) {
        add(`filters[${field}][documentId][$eq]`, val);
      } else {
        const eqOp = CASE_INSENSITIVE_FILTER_FIELDS.has(field) ? '$eqi' : '$eq';
        add(`filters[${field}][${eqOp}]`, val);
      }
      continue;
    }
    add(key, val);
  }

  parts.sort((a, b) => a[0].localeCompare(b[0]));
  return parts.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
}

export const arraySelectCamelCaseKeys = (items, keys) => {
  if (!items) return [];
  return items.map((item) => itemSelectCamelCaseKeys(item, keys));
};

/** When slug filter returns multiple rows, prefer thumbnail + earliest createdAt (May migrate batch). */
export function pickBestProjectMatch(results) {
  if (!results?.length) return null;
  if (results.length === 1) return results[0];
  return results.reduce((best, cur) => {
    const bestUrl = best?.thumbnail?.url;
    const curUrl = cur?.thumbnail?.url;
    if (curUrl && !bestUrl) return cur;
    if (bestUrl && !curUrl) return best;
    const bt = best?.createdAt ? new Date(best.createdAt).getTime() : Number.POSITIVE_INFINITY;
    const ct = cur?.createdAt ? new Date(cur.createdAt).getTime() : Number.POSITIVE_INFINITY;
    return ct < bt ? cur : best;
  });
}

export const buildStrapiParams = (filters) => {
  return encodeStrapiV5Params(filters);
};

/** Strapi 5 REST find-one uses documentId in the path; numeric `id` needs a filter query. */
export function resolveDocumentId(ref) {
  if (ref == null || ref === '') return null;
  if (typeof ref === 'object') {
    if (ref.documentId) return String(ref.documentId);
    const raw = ref.id;
    if (typeof raw === 'string' && /^[a-z0-9]{16,}$/i.test(raw)) return raw;
    return raw != null ? String(raw) : null;
  }
  const s = String(ref);
  if (/^[a-z0-9]{16,}$/i.test(s)) return s;
  return s;
}

function isLegacyMongoObjectId(value) {
  return typeof value === 'string' && /^[0-9a-f]{24}$/i.test(value);
}

function isStrapiDocumentId(value) {
  return typeof value === 'string' && /^[a-z0-9]{16,}$/i.test(value) && !isLegacyMongoObjectId(value);
}

export const itemSelectCamelCaseKeys = (item, keys) => {
  if (!item) return item;
  const nObj = {};
  keys.forEach((key) => {
    const camelKey = legacyKeyToCamelCase(key);
    const nObjKey =
      key === 'CMSName'
        ? 'cmsName'
        : key.indexOf(' ') >= 0
          ? helpers.toCamelCase(key)
          : helpers.lowercaseFirstLetter(key);
    const legacyCamelKey = key === 'CMSName' ? 'cmsName' : helpers.lowercaseFirstLetter(key);
    let val =
      key === 'id' && item.documentId !== undefined
        ? item.documentId
        : key === 'id' && item.id !== undefined
          ? item.id
          : item[camelKey] !== undefined
            ? item[camelKey]
            : item[legacyCamelKey] !== undefined
              ? item[legacyCamelKey]
              : item[key];
    if (key === 'id' && item.documentId !== undefined) {
      nObj.documentId = item.documentId;
    }
    if (val === undefined && ARRAY_FIELDS.has(nObjKey)) {
      val = [];
    }
    nObj[nObjKey] = val;
  });
  return helpers.cleanObj(nObj);
};

function appendPopulateSpec(next, populateInner, prefix = 'populate') {
  if (!populateInner || typeof populateInner !== 'object') return;

  Object.keys(populateInner).forEach((key) => {
    const value = populateInner[key];
    if (!value || !POPULATABLE_FIELDS.has(key)) return;

    if (value === true) {
      if (STRAPI_MEDIA_POPULATE_FIELDS.has(key)) {
        next[`${prefix}[${key}]`] = 'true';
      } else {
        next[`${prefix}[${key}][populate]`] = '*';
      }
      return;
    }

    if (typeof value === 'object' && value.populate && typeof value.populate === 'object') {
      Object.keys(value.populate).forEach((nestedKey) => {
        if (!value.populate[nestedKey]) return;
        const nestedPrefix = `${prefix}[${key}][populate]`;
        if (STRAPI_MEDIA_POPULATE_FIELDS.has(nestedKey)) {
          next[`${nestedPrefix}[${nestedKey}]`] = 'true';
        } else {
          next[`${nestedPrefix}[${nestedKey}][populate]`] = '*';
        }
      });
    }
  });
}

function mergePopulateIntoParams(data, spec) {
  const next = { ...data };
  if (Object.keys(next).some((k) => k === 'populate' || k.startsWith('populate['))) {
    return next;
  }
  const inner = spec && spec.populate && spec.populate.populate;
  if (inner && typeof inner === 'object') {
    const keys = Object.keys(inner).filter((k) => inner[k] && POPULATABLE_FIELDS.has(k));
    if (keys.length) {
      appendPopulateSpec(next, inner);
      return next;
    }
  }
  next.populate = '*';
  return next;
}

export const loadEndpoint = (endpoint, id, data, populateSpecArg) => {
  // Match legacy Strapi v3: exclude soft-deleted entries unless caller overrides.
  data = { SoftDelete_ne: true, _limit: 0, ...data };
  const spec =
    populateSpecArg ||
    (apiValues.getPopulateSpecForEndpoint ? apiValues.getPopulateSpecForEndpoint(endpoint) : null);
  data = mergePopulateIntoParams(data, spec);

  const base = `${apiValues.getCmsPath()}/api/${endpoint}`;
  let extra = '';
  const resolvedId = id != null && id !== '' ? resolveDocumentId(id) : null;
  if (resolvedId && isStrapiDocumentId(resolvedId)) {
    const q = Object.keys(data).length ? `?${encodeStrapiV5Params(data)}` : '';
    extra = `/${encodeURIComponent(resolvedId)}${q}`;
  } else if (resolvedId) {
    data['filters[id][$eq]'] = resolvedId;
    const q = Object.keys(data).length ? `?${encodeStrapiV5Params(data)}` : '';
    extra = q;
  } else {
    const q = Object.keys(data).length ? `?${encodeStrapiV5Params(data)}` : '';
    extra = q;
  }

  const url = base + extra;
  if (loadedEndpoints[url]) {
    const cached = loadedEndpoints[url];
    return Promise.resolve(Array.isArray(cached) && resolvedId && !isStrapiDocumentId(resolvedId) ? cached[0] : cached);
  }

  return fetch(url)
    .then((response) => {
      if (!response || !response.ok) {
        const msg = `loadEndpoint failed ${url} -> ${response ? response.status : 'no response'}`;
        console.error(msg);
        return Promise.reject(new Error(response ? `HTTP ${response.status}` : 'No response'));
      }
      return response;
    })
    .then(helpers.parseJSON)
    .then((json) => unwrapStrapiResponse(json))
    .then((results) => {
      const normalized =
        Array.isArray(results) && resolvedId && !isStrapiDocumentId(resolvedId) ? results[0] : results;
      loadedEndpoints[url] = normalized;
      return normalized;
    });
};

/** GET /api/{single-type} — unwraps a single published document (not a list). */
export const loadSingleType = (endpoint, data = {}, populateSpecArg) => {
  const spec =
    populateSpecArg ||
    (apiValues.getPopulateSpecForEndpoint ? apiValues.getPopulateSpecForEndpoint(endpoint) : null);
  return loadEndpoint(endpoint, null, data, spec).then((results) => {
    if (results == null) return null;
    if (Array.isArray(results)) return results[0] || null;
    return results;
  });
};

export const loadEndpointByFilters = (endpoint, filters) => {
  const populateSpec = apiValues.getPopulateSpecForEndpoint
    ? apiValues.getPopulateSpecForEndpoint(endpoint)
    : null;
  return loadEndpoint(endpoint, null, filters, populateSpec);
};

/**
 * Load rows on the inverse side of a mappedBy relation (parent does not embed children in REST).
 * @param {string} endpoint plural API segment, e.g. "m-list-navs"
 * @param {string} relationField relation attribute on the child type pointing at parent, e.g. "mHeaders"
 * @param {string} parentDocumentId Strapi documentId of the parent
 */
export const loadInverseRelationList = (endpoint, relationField, parentDocumentId, extra = {}) => {
  const filters = {
    [`filters[${relationField}][documentId][$eq]`]: parentDocumentId,
    _limit: 200,
    ...extra,
  };
  return loadEndpoint(endpoint, null, filters);
};

export const loadNormalizedContent = (typeName, id) => {
  const ref = apiValues.nref[typeName];
  if (!ref) return Promise.resolve(null);
  const typeEndpoint = ref.endpoint;
  const populateSpec = apiValues.getPopulateSpecForEndpoint(typeEndpoint);
  return loadEndpoint(typeEndpoint, id, {}, populateSpec).then((result) => {
    if (!result) return result;
    if (Array.isArray(result)) {
      return arraySelectCamelCaseKeys(result, ref.fieldsUsed);
    }
    return itemSelectCamelCaseKeys(result, ref.fieldsUsed);
  });
};

export const loadProjectsByTitle = (id, title) => {
  return loadEndpoint(apiValues.nref.project.endpoint, id, {
    'filters[title][$containsi]': title,
  }).then((results) => {
    const list = Array.isArray(results) ? results : results ? [results] : [];
    return {
      suggestedProjects: list.length
        ? arraySelectCamelCaseKeys(list, apiValues.nref.project.fieldsUsed)
        : [],
      viewProjectsNear: list.length
        ? arraySelectCamelCaseKeys(list, apiValues.nref.project.fieldsUsed)
        : [],
    };
  });
};

export const postToMailChimp = (email, tags) => {
  const base = apiValues.getCmsPath();
  return fetch(`${base}/api/newsoptin`, {
    method: 'post',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, tags }),
  }).catch((err) => console.log('---error---', err.message));
};
